import type {
    BookingWidgetColorScheme,
    BookingWidgetThemeTokens,
    BookingWidgetVisualThemeId,
} from './types';

const SCHEME_PALETTE: Record<
    BookingWidgetColorScheme,
    Pick<BookingWidgetThemeTokens, 'surface' | 'surfaceMuted' | 'text' | 'textMuted' | 'border'>
> = {
    light: {
        surface: 'rgba(255, 255, 255, 0.85)',
        surfaceMuted: 'rgba(248, 250, 252, 0.8)',
        text: '#1e293b',
        textMuted: '#64748b',
        border: 'rgba(226, 232, 240, 0.8)',
    },
    dark: {
        surface: 'rgba(45, 45, 45, 0.85)',
        surfaceMuted: 'rgba(55, 55, 55, 0.6)',
        text: '#f1f5f9',
        textMuted: '#9ca3af',
        border: 'rgba(75, 85, 99, 0.5)',
    },
};

export function resolveBookingWidgetTokens(input: {
    visualTheme: BookingWidgetVisualThemeId;
    colorScheme: BookingWidgetColorScheme;
    primaryColor?: string | null;
    secondaryColor?: string | null;
    themeOptions?: Record<string, unknown>;
}): BookingWidgetThemeTokens {
    const scheme = input.colorScheme === 'dark' ? 'dark' : 'light';
    const palette = SCHEME_PALETTE[scheme];
    const primary = input.primaryColor || (scheme === 'dark' ? '#ffffff' : '#0f172a');
    const secondary = input.secondaryColor || '#BBBBBB';

    const sleekSubtle = input.visualTheme === 'sleek' && input.themeOptions?.card_blur === 'sm';
    const cardBlur =
        input.visualTheme === 'minimal'
            ? '0px'
            : input.visualTheme === 'sleek'
              ? sleekSubtle
                  ? '4px'
                  : '20px'
              : '16px';

    const radius =
        input.visualTheme === 'minimal'
            ? '0.75rem'
            : input.visualTheme === 'immersive'
              ? '0.25rem'
              : '1rem';

    const solidSurface =
        input.visualTheme === 'minimal'
            ? scheme === 'dark'
                ? '#1f2937'
                : '#ffffff'
            : input.visualTheme === 'immersive'
              ? scheme === 'dark'
                  ? 'rgba(17, 24, 39, 0.84)'
                  : 'rgba(255, 255, 255, 0.9)'
              : sleekSubtle
                ? scheme === 'dark'
                    ? 'rgba(45, 45, 45, 0.92)'
                    : 'rgba(255, 255, 255, 0.92)'
                : scheme === 'dark'
                  ? 'rgba(45, 45, 45, 0.18)'
                  : 'rgba(255, 255, 255, 0.16)';

    return {
        primary,
        secondary,
        surface: solidSurface,
        surfaceMuted: palette.surfaceMuted,
        text: palette.text,
        textMuted: palette.textMuted,
        border: palette.border,
        pageBackground: secondary,
        radius,
        shadow:
            input.visualTheme === 'minimal'
                ? '0 1px 2px rgba(15, 23, 42, 0.06)'
                : input.visualTheme === 'immersive'
                  ? '0 4px 24px rgba(15, 23, 42, 0.1)'
                  : '0 10px 40px rgba(15, 23, 42, 0.12)',
        cardBlur,
        isDark: scheme === 'dark',
    };
}

export function tokensToCssVars(tokens: BookingWidgetThemeTokens): Record<string, string> {
    return {
        '--bw-primary': tokens.primary,
        '--bw-secondary': tokens.secondary,
        '--bw-surface': tokens.surface,
        '--bw-surface-muted': tokens.surfaceMuted,
        '--bw-text': tokens.text,
        '--bw-text-muted': tokens.textMuted,
        '--bw-border': tokens.border,
        '--bw-page-bg': tokens.pageBackground,
        '--bw-radius': tokens.radius,
        '--bw-shadow': tokens.shadow,
        '--bw-card-blur': tokens.cardBlur,
    };
}

export type GlassCardStyle = {
    backgroundColor: string;
    backdropFilter?: string;
    WebkitBackdropFilter?: string;
};

/** Frosted glass fill driven by `tokens.cardBlur` (sleek Subtle vs Standard). */
export function glassCardStyle(tokens: BookingWidgetThemeTokens): GlassCardStyle {
    if (tokens.cardBlur === '0px') {
        return { backgroundColor: tokens.surface };
    }

    const filter = `blur(${tokens.cardBlur})`;

    return {
        backgroundColor: tokens.surface,
        backdropFilter: filter,
        WebkitBackdropFilter: filter,
    };
}

export type ImmersiveParentCardStyle = {
    backgroundColor: string;
    borderColor: string;
    borderRadius: string;
    boxShadow: string;
};

/** Solid parent panel that wraps immersive inner content cards. */
export function immersiveParentCardStyle(
    tokens: BookingWidgetThemeTokens,
    secondaryColor?: string | null,
): ImmersiveParentCardStyle {
    return {
        backgroundColor: secondaryColor || tokens.secondary,
        borderColor: tokens.border,
        borderRadius: tokens.radius,
        boxShadow: tokens.shadow,
    };
}
